import React, {
  FC,
  Children,
  PropsWithChildren,
  ComponentPropsWithoutRef,
} from 'react'
import {
  CarouselState,
  CarouselRoot,
  CarouselControls,
  UnstyledCarouselScrollArea,
  CarouselPreviousButton,
  CarouselNextButton,
  CarouselItem,
} from '@moonpig/launchpad-components'
import { styled, breakpointUp, breakpointDown } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'

export type Props = {
  hideScrollButtons?: boolean
  itemsGap?: { xs?: number; md?: number }
  $noSpacing?: boolean
  ariaLabel: string
  skipLinkHref?: string
  children: React.ReactNode
  alignItems?: string
}

const StyledCarouselItem = styled(CarouselItem)`
  overflow: visible;
  &:first-child {
    ${s({ ml: { xs: 5, md: 0 } })}
  }

  &:last-child {
    ${s({ mr: { xs: 5, md: 0 } })}
  }
`

const StyledCarouselScrollArea = styled(UnstyledCarouselScrollArea)<{
  centerSlides?: boolean
  gap?: { xs?: number; md?: number }
  alignItems?: string
}>`
  ${({ gap, alignItems }) =>
    s({
      gap: gap || { xs: 12, md: 16 },
      alignItems,
    })};

  ${breakpointUp('md')} {
    justify-content: ${({ centerSlides }) =>
      centerSlides ? 'center' : 'initial'};
  }
`

const StyledPreviousButton = styled(CarouselPreviousButton)`
  ${breakpointDown('md')} {
    display: none;
  }
`

const StyledNextButton = styled(CarouselNextButton)`
  ${breakpointDown('md')} {
    display: none;
  }
`

type StyledCarouselRootProps = ComponentPropsWithoutRef<typeof CarouselRoot>

const StyledCarouselRoot = styled(
  ({ ...props }: StyledCarouselRootProps & { $noSpacing: boolean }) => (
    <CarouselRoot {...props} />
  ),
)<{ $noSpacing: boolean }>`
  ${({ $noSpacing }) =>
    s({
      mx: $noSpacing ? 0 : -6,
      px: $noSpacing ? 0 : 6,
    })};

  ${breakpointDown('md')} {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
`

export const Carousel: FC<PropsWithChildren<Props>> = ({
  hideScrollButtons,
  itemsGap,
  children,
  $noSpacing = false,
  ariaLabel,
  skipLinkHref,
  alignItems = '',
  ...rest
}) => {
  return (
    <CarouselState>
      <StyledCarouselRoot
        aria-label={ariaLabel}
        $noSpacing={$noSpacing}
        skipLinkHref={skipLinkHref}
        {...rest}
      >
        <CarouselControls>
          {!hideScrollButtons && <StyledPreviousButton />}
          {!hideScrollButtons && <StyledNextButton />}
        </CarouselControls>
        <StyledCarouselScrollArea
          alignItems={alignItems}
          centerSlides={hideScrollButtons}
          data-testid="lp-components-carousel-scroll-area"
          gap={itemsGap}
        >
          {Children.map(children, (item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <StyledCarouselItem key={index} tabIndex={undefined}>
              {item}
            </StyledCarouselItem>
          ))}
        </StyledCarouselScrollArea>
      </StyledCarouselRoot>
    </CarouselState>
  )
}
